import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const ArticleReader = ({ article, onBack, relatedArticles }) => {
  const navigate = useNavigate();
  const [isHelpful, setIsHelpful] = useState(null);
  const [showTableOfContents, setShowTableOfContents] = useState(true);

  // Intercept in-app article links so they don't cause a full-page reload
  const handleInternalLink = (href) => {
    if (href?.startsWith('/knowledge-base-articles')) {
      navigate(href);
      return true;
    }
    return false;
  };

  // Use the article's real content when available; fall back to a placeholder.
  const articleContent = article?.content || `
# ${article?.title}

## Introduction

This comprehensive guide will walk you through everything you need to know about ${article?.title?.toLowerCase()}.

## Coming Soon

Full documentation for this topic is being written. In the meantime, please submit a support ticket if you need help.
  `;

  // Convert a heading text to a URL-safe ID (same function used in link hrefs and heading id attrs)
  const slugify = (text) =>
    String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  // Derive a simple table of contents from the markdown headings
  const tableOfContents = (articleContent.match(/^#{2,3} .+$/gm) || [])
    .map(h => {
      const text = h.replace(/^#+\s*/, '');
      return { id: slugify(text), title: text };
    })
    .slice(0, 10);

  const handleFeedback = (helpful) => {
    setIsHelpful(helpful);
    toast?.success(helpful ? 'Thank you for your feedback!' : 'We\'ll work on improving this article');
  };

  const handlePrint = () => {
    window?.print();
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500/10 text-green-500';
      case 'Intermediate':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'Advanced':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="flex gap-6">
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-border">
            <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={onBack}
              className="mb-4"
            >
              Back to Articles
            </Button>

            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${getDifficultyColor(article?.difficulty)}`}>
                    {article?.difficulty}
                  </span>
                  <span className="px-2.5 py-1 bg-muted text-muted-foreground rounded-md text-xs font-medium">
                    {article?.contentType}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {article?.category}
                  </span>
                </div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-3">
                  {article?.title}
                </h1>
                <p className="text-muted-foreground">
                  {article?.description}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  <span>{article?.readTime}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Eye" size={14} />
                  <span>{article?.views?.toLocaleString()} views</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  <span>Updated {new Date(article?.lastUpdated)?.toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="text-yellow-500 fill-yellow-500" />
                  <span>{article?.rating}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" iconName="Printer" onClick={handlePrint} />
                <Button variant="ghost" size="sm" iconName="Share2" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 prose prose-slate dark:prose-invert max-w-none">
            <ReactMarkdown
              components={{
                img: ({ src, alt }) => (
                  <img
                    src={src}
                    alt={alt || ''}
                    className="rounded-lg border border-border shadow-md max-w-full my-6 mx-auto block"
                    loading="lazy"
                  />
                ),
                // Add matching IDs to headings so TOC anchor links work correctly.
                h2: ({ children, ...rest }) => {
                  const text = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
                  return <h2 id={slugify(text)} {...rest}>{children}</h2>;
                },
                h3: ({ children, ...rest }) => {
                  const text = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
                  return <h3 id={slugify(text)} {...rest}>{children}</h3>;
                },
                // Intercept internal article links — use client-side navigation
                // so clicking "Next steps" links inside articles doesn't reload the page.
                a: ({ href, children, ...rest }) => {
                  if (href?.startsWith('/knowledge-base-articles')) {
                    return (
                      <a
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(href);
                        }}
                        {...rest}
                      >
                        {children}
                      </a>
                    );
                  }
                  return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
                },
              }}
            >
              {articleContent}
            </ReactMarkdown>

            {/* Help-center reference screenshot shown at the bottom of every article */}
            <hr className="my-6 border-border" />
            <p className="text-sm text-muted-foreground mb-3">
              Browse all 19 help articles in the <strong>Help &amp; Support</strong> section:
            </p>
            <img
              src="/assets/help/support-tickets.jpg"
              alt="The WebForms.biz Help Center Knowledge Base — search and browse articles"
              className="rounded-lg border border-border shadow-md max-w-full my-2 mx-auto block"
              loading="lazy"
            />
          </div>

          {/* Feedback */}
          <div className="p-6 border-t border-border bg-muted/30">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Was this article helpful?
              </h3>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant={isHelpful === true ? 'default' : 'outline'}
                  size="sm"
                  iconName="ThumbsUp"
                  iconPosition="left"
                  onClick={() => handleFeedback(true)}
                >
                  Yes, helpful
                </Button>
                <Button
                  variant={isHelpful === false ? 'default' : 'outline'}
                  size="sm"
                  iconName="ThumbsDown"
                  iconPosition="left"
                  onClick={() => handleFeedback(false)}
                >
                  No, not helpful
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles?.length > 0 && (
          <div className="mt-6 bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
              Related Articles
            </h3>
            <div className="space-y-3">
              {relatedArticles?.map((related) => (
                <button
                  key={related?.id}
                  onClick={() => navigate(`/knowledge-base-articles?article=${related?.id}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-smooth text-left group"
                >
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-smooth mb-1">
                      {related?.title}
                    </h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{related?.readTime}</span>
                      <span>•</span>
                      <span>{related?.difficulty}</span>
                    </div>
                  </div>
                  <Icon name="ArrowRight" size={16} className="text-muted-foreground group-hover:text-primary transition-smooth" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Table of Contents */}
      <div className="hidden xl:block w-64 flex-shrink-0">
        <div className="bg-card border border-border rounded-lg p-4 sticky top-24">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">Table of Contents</h3>
            <button
              onClick={() => setShowTableOfContents(!showTableOfContents)}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name={showTableOfContents ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </button>
          </div>
          {showTableOfContents && (
            <div className="space-y-2">
              {tableOfContents?.map((item) => (
                <a
                  key={item?.id}
                  href={`#${item?.id}`}
                  className="block text-sm text-muted-foreground hover:text-primary transition-smooth py-1"
                >
                  {item?.title}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleReader;